import { useCallback, useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import ScreenHeader from "@/components/mnf/ScreenHeader";
import { GlassCard, Btn, Avatar } from "@/components/mnf/UI";
import Icon from "@/components/mnf/Icon";
import {
  useStoreQuery,
  useStoreMutation,
  queries,
  mutations,
  type NotificationPrefs,
  type InjuryFlag,
} from "@/lib/store";
import { toast } from "@/hooks/use-toast";
import {
  isPushSupported,
  isSubscribedToPush,
  subscribeToPush,
  unsubscribeFromPush,
} from "@/lib/push";

/**
 * Settings
 *
 * The single place a sister manages her account preferences. v1 ships
 * with the notification preferences UI (matching Privacy Policy Section 9)
 * and stub links to the Legal hub. Future settings (account, billing,
 * accessibility, theme) will land in this same file.
 */

interface CategoryToggle {
  key: keyof Pick<
    NotificationPrefs,
    | "scriptureReminders"
    | "prayerWallReplies"
    | "weeklyCheckIns"
    | "coachingMessages"
    | "communityAndLive"
  >;
  label: string;
  helper: string;
  icon: string;
}

const CATEGORY_TOGGLES: CategoryToggle[] = [
  {
    key: "scriptureReminders",
    label: "Daily scripture reminder",
    helper: "A short verse at the time you choose.",
    icon: "menu_book",
  },
  {
    key: "prayerWallReplies",
    label: "Prayer wall replies",
    helper: "Hearts and notes from sisters on your requests.",
    icon: "favorite",
  },
  {
    key: "weeklyCheckIns",
    label: "Weekly check-ins",
    helper: "Sunday evening reflection and streak nudges.",
    icon: "calendar_today",
  },
  {
    key: "coachingMessages",
    label: "Notes from Coach Kiley",
    helper: "Coaching messages and weekly compliance summaries.",
    icon: "chat_bubble",
  },
  {
    key: "communityAndLive",
    label: "Live classes and gatherings",
    helper: "Five-minute warning before live events open.",
    icon: "groups",
  },
];

/* A small toggle that matches our brand tokens. iOS-style pill, forest
 * when on, muted neutral when off. */
function Toggle({
  checked,
  onChange,
  disabled,
  testId,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      data-testid={testId}
      style={{
        position: "relative",
        width: 46,
        height: 28,
        borderRadius: 999,
        border: 0,
        background: disabled
          ? "rgba(45, 62, 48, 0.15)"
          : checked
            ? "#1E4A35"
            : "rgba(45, 62, 48, 0.25)",
        cursor: disabled ? "not-allowed" : "pointer",
        transition: "background 200ms ease",
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
      }}
    >
      <span
        style={{
          position: "absolute",
          top: 3,
          left: checked ? 21 : 3,
          width: 22,
          height: 22,
          borderRadius: 999,
          background: "#F5F0E8",
          boxShadow: "0 1px 3px rgba(0,0,0,0.18)",
          transition: "left 200ms ease",
        }}
      />
    </button>
  );
}

export default function Settings() {
  const [, navigate] = useLocation();
  const me = useStoreQuery(queries.currentUser);
  // No fallback to the seeded demo account: every handler below WRITES with this id, so
  // the fallback saved a real sister's notification preferences onto the
  // seeded demo account.
  const userId = me?.id ?? null;
  const prefs = useStoreQuery(() =>
    userId ? queries.notificationPrefsForUser(userId) : null,
  );
  const setPermission = useStoreMutation(mutations.setNotificationPermission);
  const updatePrefs = useStoreMutation(mutations.updateNotificationPrefs);

  // Real browser/subscription state, not the stored flag. The stored flag was
  // the only thing this screen consulted, so it happily reported
  // "On. Your device will deliver reminders below." while
  // Notification.requestPermission() had never been called and no push
  // subscription existed. That is a UI lying to a paying customer.
  const [subscribed, setSubscribed] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);
  const supported = isPushSupported();

  const refreshSubscription = useCallback(() => {
    if (!supported) {
      setSubscribed(false);
      return;
    }
    void isSubscribedToPush().then(setSubscribed);
  }, [supported]);

  useEffect(refreshSubscription, [refreshSubscription]);

  // "On" now means: the browser granted permission AND a push subscription
  // actually exists.
  const granted = subscribed;

  async function handleTogglePermission(next: boolean) {
    if (!userId || pushBusy) return;
    setPushBusy(true);
    try {
      const result = next ? await subscribeToPush() : await unsubscribeFromPush();
      if (!result.ok) {
        toast({
          variant: "destructive",
          title: next ? "We couldn't turn on reminders" : "We couldn't turn these off",
          description: result.reason ?? "Please try again in a moment.",
        });
        return;
      }
      // Only record the preference once the browser actually agreed.
      setPermission(userId, next ? "granted" : "denied");
    } finally {
      setPushBusy(false);
      refreshSubscription();
    }
  }

  function handleToggleCategory(
    key: CategoryToggle["key"],
    next: boolean,
  ) {
    if (!userId) return;
    updatePrefs(userId, { [key]: next });
  }

  function handleScriptureTime(time: string) {
    if (!userId) return;
    updatePrefs(userId, { scriptureTime: time });
  }

  // Identity not resolved yet. Rendering the form here would show, and then
  // save, the seeded demo account's preferences.
  if (!userId || !prefs) {
    return (
      <div className="pb-24" data-testid="page-settings">
        <ScreenHeader
          variant="forest"
          eyebrow="Your preferences"
          title="Settings"
          subtitle="Tend the small dials. We keep them honest."
        />
        <div className="px-5 mt-6">
          <p className="mnf-body" style={{ color: "var(--mnf-body)", fontSize: 14 }}>
            Loading your settings...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pb-24" data-testid="page-settings">
      <ScreenHeader
        variant="forest"
        eyebrow="Your preferences"
        title="Settings"
        subtitle="Tend the small dials. We keep them honest."
      />

      {/* Notifications section */}
      <div className="px-5 mt-2">
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-f)", marginBottom: 8 }}
        >
          Notifications
        </p>

        <GlassCard organic padding={14} className="mb-2.5">
          <div className="flex items-start gap-3">
            <Icon
              name="notifications_active"
              size={22}
              color={granted ? "#1E4A35" : "rgba(45, 62, 48, 0.5)"}
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                  <p
                    className="mnf-serif"
                    style={{
                      color: "var(--mnf-f)",
                      fontSize: 15,
                      fontWeight: 600,
                      lineHeight: 1.3,
                    }}
                  >
                    Push notifications
                  </p>
                  <p
                    className="mnf-body mt-1"
                    style={{
                      color: "var(--mnf-body)",
                      fontSize: 13,
                      lineHeight: 1.4,
                    }}
                  >
                    {!supported
                      ? "This browser can't deliver push notifications."
                      : granted
                        ? "On. This device is set up to receive the reminders below."
                        : prefs?.permissionStatus === "unknown"
                          ? "We have not asked yet. Tap to set this up."
                          : "Off. We will not send you notifications."}
                  </p>
                </div>
                {prefs.permissionStatus === "unknown" ? (
                  <Btn
                    variant="primary"
                    onClick={() => navigate("/notifications/permission")}
                    data-testid="button-setup-notifications"
                  >
                    Set up
                  </Btn>
                ) : (
                  <Toggle
                    checked={granted}
                    onChange={handleTogglePermission}
                    testId="toggle-permission"
                  />
                )}
              </div>
            </div>
          </div>
        </GlassCard>

        {prefs.permissionStatus !== "unknown" && (
          <>
            <div className="space-y-2">
              {CATEGORY_TOGGLES.map((cat) => {
                const checked = Boolean(prefs[cat.key]);
                return (
                  <GlassCard
                    key={cat.key}
                    organic
                    padding={14}
                    data-testid={`row-${cat.key}`}
                  >
                    <div className="flex items-start gap-3">
                      <div
                        className="shrink-0 flex items-center justify-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 10,
                          background: granted
                            ? "rgba(168, 212, 184, 0.25)"
                            : "rgba(45, 62, 48, 0.08)",
                        }}
                      >
                        <Icon
                          name={cat.icon}
                          size={18}
                          color={granted ? "#1E4A35" : "rgba(45, 62, 48, 0.5)"}
                        />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p
                          className="mnf-serif"
                          style={{
                            color: granted
                              ? "var(--mnf-f)"
                              : "rgba(45, 62, 48, 0.55)",
                            fontSize: 14,
                            fontWeight: 600,
                            lineHeight: 1.3,
                          }}
                        >
                          {cat.label}
                        </p>
                        <p
                          className="mnf-body mt-0.5"
                          style={{
                            color: "var(--mnf-body)",
                            fontSize: 12.5,
                            lineHeight: 1.4,
                            opacity: granted ? 1 : 0.6,
                          }}
                        >
                          {cat.helper}
                        </p>
                        {cat.key === "scriptureReminders" &&
                          checked &&
                          granted && (
                            <div className="mt-2.5 flex items-center gap-2">
                              <Icon
                                name="schedule"
                                size={15}
                                color="rgba(45, 62, 48, 0.7)"
                              />
                              <span
                                className="mnf-body"
                                style={{
                                  color: "var(--mnf-body)",
                                  fontSize: 12.5,
                                }}
                              >
                                Send at
                              </span>
                              <input
                                type="time"
                                value={prefs.scriptureTime}
                                onChange={(e) =>
                                  handleScriptureTime(e.target.value)
                                }
                                className="mnf-body"
                                style={{
                                  fontSize: 12.5,
                                  padding: "4px 8px",
                                  borderRadius: 8,
                                  border: "1px solid rgba(45,62,48,0.18)",
                                  background: "rgba(245,240,232,0.6)",
                                  color: "var(--mnf-f)",
                                }}
                                data-testid="input-scripture-time"
                              />
                            </div>
                          )}
                      </div>
                      <Toggle
                        checked={checked}
                        onChange={(next) =>
                          handleToggleCategory(cat.key, next)
                        }
                        disabled={!granted}
                        testId={`toggle-${cat.key}`}
                      />
                    </div>
                  </GlassCard>
                );
              })}
            </div>

            <div className="mt-3">
              <GlassCard organic padding={12}>
                <div className="flex items-start gap-2">
                  <Icon name="info" size={16} color="rgba(45,62,48,0.6)" />
                  <p
                    className="mnf-body"
                    style={{
                      color: "var(--mnf-body)",
                      fontSize: 12,
                      lineHeight: 1.45,
                    }}
                  >
                    Transactional alerts (billing, security, password) are sent
                    even when categories are off, so you do not lose access by
                    accident. We never use push for advertising.
                  </p>
                </div>
              </GlassCard>
            </div>
          </>
        )}
      </div>

      {/* Account section */}
      <div className="px-5 mt-6">
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-f)", marginBottom: 8 }}
        >
          Account
        </p>
        <Link href="/profile" className="no-underline" data-testid="link-settings-profile">
          <GlassCard organic padding={14}>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar
                    initials={(me?.name ?? "S").split(/\s+/).map((p) => p[0]).filter(Boolean).slice(0, 2).join("").toUpperCase()}
                    src={me?.avatarDataUrl ?? undefined}
                    size={42}
                  />
                  <div>
                    <p
                      className="mnf-serif"
                      style={{
                        color: "var(--mnf-f)",
                        fontSize: 14,
                        fontWeight: 600,
                      }}
                    >
                      {me?.name ?? "Sister"}
                    </p>
                    <p
                      className="mnf-body"
                      style={{
                        color: "var(--mnf-body)",
                        fontSize: 12,
                        opacity: 0.75,
                      }}
                    >
                      View profile
                    </p>
                  </div>
                </div>
                <Icon name="chevron_right" size={20} color="#1E4A35" />
              </div>
            </div>
          </GlassCard>
        </Link>
      </div>

      {/* Membership section */}
      <div className="px-5 mt-6">
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-f)", marginBottom: 8 }}
        >
          Membership
        </p>
        <GlassCard organic padding={0}>
          <Link
            href="/billing"
            className="flex items-center gap-3 px-3.5 py-3"
            style={{ textDecoration: "none" }}
            data-testid="link-settings-billing"
          >
            <Icon name="credit_card" size={20} color="#1E4A35" />
            <span
              className="mnf-serif flex-1"
              style={{ color: "var(--mnf-f)", fontSize: 14, fontWeight: 600 }}
            >
              Billing and renewal reminders
            </span>
            <Icon name="arrow_forward" size={16} color="rgba(45,62,48,0.5)" />
          </Link>
        </GlassCard>
      </div>

      {/* Movement section */}
      <MovementSection userId={userId} />

      {/* Privacy and policies section */}
      <div className="px-5 mt-6">
        <p
          className="mnf-eyebrow"
          style={{ color: "var(--mnf-f)", marginBottom: 8 }}
        >
          Privacy and policies
        </p>
        <GlassCard organic padding={0}>
          <Link
            href="/legal"
            className="flex items-center gap-3 px-3.5 py-3"
            style={{ textDecoration: "none" }}
            data-testid="link-settings-legal"
          >
            <Icon name="gavel" size={20} color="#1E4A35" />
            <span
              className="mnf-serif flex-1"
              style={{ color: "var(--mnf-f)", fontSize: 14, fontWeight: 600 }}
            >
              Our policies and your privacy
            </span>
            <Icon name="arrow_forward" size={16} color="rgba(45,62,48,0.5)" />
          </Link>
          <div
            style={{
              height: 1,
              background: "rgba(45,62,48,0.08)",
              margin: "0 14px",
            }}
          />
          <Link
            href="/legal/privacy"
            className="flex items-center gap-3 px-3.5 py-3"
            style={{ textDecoration: "none" }}
            data-testid="link-settings-privacy"
          >
            <Icon name="shield" size={20} color="#1E4A35" />
            <span
              className="mnf-serif flex-1"
              style={{ color: "var(--mnf-f)", fontSize: 14, fontWeight: 600 }}
            >
              Privacy Policy
            </span>
            <Icon name="arrow_forward" size={16} color="rgba(45,62,48,0.5)" />
          </Link>
          <div
            style={{
              height: 1,
              background: "rgba(45,62,48,0.08)",
              margin: "0 14px",
            }}
          />
          <Link
            href="/legal/terms"
            className="flex items-center gap-3 px-3.5 py-3"
            style={{ textDecoration: "none" }}
            data-testid="link-settings-terms"
          >
            <Icon name="description" size={20} color="#1E4A35" />
            <span
              className="mnf-serif flex-1"
              style={{ color: "var(--mnf-f)", fontSize: 14, fontWeight: 600 }}
            >
              Terms of Service
            </span>
            <Icon name="arrow_forward" size={16} color="rgba(45,62,48,0.5)" />
          </Link>
        </GlassCard>
      </div>
    </div>
  );
}

/* ----------------------------- Movement panel ---------------------------- */

const INJURY_LABELS: Record<InjuryFlag, { title: string; sub: string; emoji: string }> = {
  joints: { title: "Joints", sub: "Knees, hips, shoulders, wrists, ankles", emoji: "🦵" },
  spine: { title: "Spine", sub: "Low back, neck, disc issues, scoliosis", emoji: "🌿" },
  "pelvic-floor": { title: "Pelvic floor", sub: "Diastasis, prolapse, leaking, heaviness", emoji: "🤍" },
  other: { title: "Other", sub: "Tendinitis, hernia, recovery from surgery", emoji: "🩹" },
};

function MovementSection({ userId }: { userId: string }) {
  const profile = useStoreQuery(() => queries.movementProfile(userId));
  const mesocycle = useStoreQuery(() => queries.mesocycleSummary(userId));
  const togglePaused = useStoreMutation(mutations.toggleInjuryPaused);
  const refresh = useStoreMutation(mutations.refreshMesocycle);

  return (
    <div className="px-5 mt-6">
      <p
        className="mnf-eyebrow"
        style={{ color: "var(--mnf-f)", marginBottom: 8 }}
      >
        Movement
      </p>

      {!profile || !profile.completed ? (
        <GlassCard organic padding={14}>
          <div className="flex items-start gap-3">
            <Icon name="auto_awesome" size={20} color="#1E4A35" />
            <div className="flex-1">
              <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 600, color: "var(--mnf-f)" }}>
                Personalize my training
              </p>
              <p className="mnf-body mt-1" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                Set up your NASM-aligned plan with injury awareness, goals, and weekly schedule.
              </p>
              <Link href="/onboarding" data-testid="link-settings-personalize">
                <Btn variant="primary" size="sm" className="mt-2">
                  Set up plan <Icon name="arrow_forward" size={14} />
                </Btn>
              </Link>
            </div>
          </div>
        </GlassCard>
      ) : (
        <>
          <GlassCard organic padding={14} className="mb-2.5">
            <div className="flex items-center justify-between gap-3 mb-2">
              <div>
                <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "var(--mnf-f)" }}>
                  Mesocycle
                </p>
                {mesocycle ? (
                  <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)" }}>
                    Week {mesocycle.week} of 4 · Block {mesocycle.block}
                  </p>
                ) : null}
              </div>
              <button
                onClick={() => refresh(userId)}
                data-testid="button-refresh-mesocycle"
                className="mnf-body hover-elevate active-elevate-2"
                style={{
                  padding: "8px 14px", borderRadius: 999,
                  background: "rgba(30,74,53,0.08)",
                  color: "var(--mnf-f)",
                  fontSize: 11.5, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase",
                  border: "1px solid rgba(30,74,53,0.14)",
                }}
              >
                Restart 4-week block
              </button>
            </div>
            <p className="mnf-body italic" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
              Compounds hold for four weeks, accessories rotate every two. Restart anchors a fresh block today.
            </p>
          </GlassCard>

          <p
            className="mnf-eyebrow"
            style={{ color: "var(--mnf-f)", marginTop: 14, marginBottom: 8 }}
          >
            Injuries and what to work around
          </p>

          {profile.injuries.length === 0 ? (
            <GlassCard organic padding={14}>
              <p className="mnf-body italic" style={{ fontSize: 12.5, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                No injuries flagged. Add some on the onboarding flow if anything shows up.
              </p>
            </GlassCard>
          ) : (
            <div className="space-y-2">
              {profile.injuries.map((inj) => {
                const meta = INJURY_LABELS[inj.flag];
                return (
                  <GlassCard key={inj.flag} organic padding={12} data-testid={`card-injury-${inj.flag}`}>
                    <div className="flex items-center gap-3">
                      <span style={{ fontSize: 22 }} aria-hidden>{meta.emoji}</span>
                      <div className="flex-1 min-w-0">
                        <p className="mnf-body" style={{ fontSize: 13.5, fontWeight: 700, color: "var(--mnf-fd)" }}>
                          {meta.title}
                        </p>
                        <p className="mnf-body" style={{ fontSize: 11.5, color: "var(--mnf-tg)", lineHeight: 1.4 }}>
                          {inj.detail || meta.sub}
                        </p>
                      </div>
                      <button
                        onClick={() => togglePaused(userId, inj.flag)}
                        data-testid={`toggle-injury-${inj.flag}`}
                        className="mnf-body hover-elevate active-elevate-2 shrink-0"
                        style={{
                          padding: "6px 12px", borderRadius: 999,
                          background: inj.paused ? "rgba(30,74,53,0.06)" : "var(--mnf-f)",
                          color: inj.paused ? "var(--mnf-tg)" : "#F5F0E8",
                          fontSize: 10.5, fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase",
                          border: `1px solid ${inj.paused ? "rgba(30,74,53,0.14)" : "var(--mnf-f)"}`,
                        }}
                      >
                        {inj.paused ? "Paused" : "Active"}
                      </button>
                    </div>
                  </GlassCard>
                );
              })}
              <p className="mnf-body italic" style={{ fontSize: 11, color: "var(--mnf-tg)", lineHeight: 1.5, padding: "0 4px" }}>
                Pause an injury when it's healing well to unlock more exercises. Reactivate any time.
              </p>
            </div>
          )}

          {/* Plan summary + edit link */}
          <div className="mt-4">
            <GlassCard organic padding={14}>
              <div className="flex items-start gap-3">
                <Icon name="tune" size={20} color="#1E4A35" />
                <div className="flex-1">
                  <p className="mnf-serif" style={{ fontSize: 14, fontWeight: 700, color: "var(--mnf-f)" }}>
                    My plan
                  </p>
                  <p className="mnf-body mt-1" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                    {profile.goals.length > 0
                      ? `Goals: ${profile.goals.map((g) => g.replace("-", " ")).join(", ")}`
                      : "No goals set yet"}
                  </p>
                  <p className="mnf-body" style={{ fontSize: 12, color: "var(--mnf-tg)", lineHeight: 1.5 }}>
                    {profile.schedule.days.length > 0
                      ? `${profile.schedule.days.length} days a week, ${profile.schedule.minutesPerDay} min`
                      : "No schedule set yet"}
                  </p>
                  <Link href="/plan-editor" data-testid="link-edit-plan">
                    <Btn variant="secondary" size="sm" className="mt-2">
                      Edit my plan <Icon name="arrow_forward" size={14} />
                    </Btn>
                  </Link>
                </div>
              </div>
            </GlassCard>
          </div>
        </>
      )}
    </div>
  );
}
